import React, {useContext, useEffect, useRef} from 'react';
import {gql, useQuery} from "@apollo/client";
import Progress from '../../../util/Progress';
import {Alert, AlertDescription, AlertTitle} from "@/components/ui/alert";
import CenteredNotification from "../../../util/CenteredNotification";
import {ExclamationTriangleIcon} from "@radix-ui/react-icons";
import {Navigate, useParams} from "react-router-dom";
import {getCallClientId, getCallClientIdVariables} from "./types/getCallClientId";
import {OAuthContext} from "./Provider";

const OAUTH_CLIENT_ID = gql`query getCallClientId($integration: ID) {
    calls {
        oauth(integration: $integration) {
            clientId
            redirectUri
            endpoint
        }
    }
}`;

export default function OAuthInit() {
    const {setToken, token} = useContext(OAuthContext);
    const {integration} = useParams<{ integration: string }>()
    const fired = useRef(false);

    // Get client id for oauth and redirect
    const { loading, data, error } = useQuery<getCallClientId, getCallClientIdVariables>(OAUTH_CLIENT_ID, { variables: { integration }});

    useEffect(() => {
        if (fired.current) return;
        if (loading || error || !data?.calls?.oauth) return;

        fired.current = true;
        if (!data.calls.oauth.endpoint) {
            // No OAuth needed — set empty token so Activity can proceed
            setToken({__typename: 'OAuthToken', accessToken: '', refreshToken: '', expiresIn: 0, tokenType: ''});
            return;
        }

        window.location.href = data.calls.oauth.endpoint +
            '?response_type=code&client_id=' + data.calls.oauth.clientId +
            '&redirect_uri=' + encodeURIComponent(data.calls.oauth.redirectUri) +
            '&state=';
    }, [loading, data, error, setToken]);

    if (error) {
        return (
            <CenteredNotification>
                <Alert variant="destructive">
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    <AlertTitle>Authorization error</AlertTitle>
                    <AlertDescription>Error fetching client id: {error.message}</AlertDescription>
                </Alert>
            </CenteredNotification>
        );
    }

    // Once token is set (no-OAuth path), hop to the activity page
    if (token) {
        return <Navigate to={`/calls/${integration}`} replace />
    }

    return <Progress>{loading ? 'Loading ...' : 'Redirecting ...'}</Progress>;
}
